import { Link, useLocation } from "react-router-dom";
import { useState } from "react";
import { motion } from "framer-motion";
import {
  CalendarDays,
  MapPin,
  Eye,
  ShieldCheck,
  Crown,
  Briefcase,
  GraduationCap,
} from "lucide-react";
import { createProfilePath } from "../utils/profileRoutes";
import { isVerifiedProfile } from "../utils/verification";
import { getProfileDisplayImage, shouldShowProfilePlaceholder } from "../utils/profilePlaceholder";
import { isPremiumActive } from "../utils/membership";

function formatDate(value) {
  if (!value) return "—";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return "—";
  return d.toLocaleDateString("en-GB").replaceAll("/", "-");
}

/**
 * ProfileAvatar — used everywhere we render a profile thumbnail
 */
export const ProfileAvatar = ({ profile, size = 64, className = "" }) => {
  const [loaded, setLoaded] = useState(false);
  const dim = `${size}px`;
  const photo = getProfileDisplayImage(profile);
  const isPlaceholder = shouldShowProfilePlaceholder(profile);

  return (
    <div style={{ width: dim, height: dim }} className={`relative shrink-0 overflow-hidden rounded-2xl ${className}`}>
      {!loaded && !isPlaceholder && (
        <div className="absolute inset-0 animate-pulse bg-gradient-to-r from-sand-200 via-sand-100 to-sand-200 bg-[length:800px_100%] rounded-2xl" />
      )}
      <img
        src={photo}
        alt={profile?.fullName || "Profile"}
        style={{ width: dim, height: dim }}
        className={`rounded-2xl object-cover object-center ring-1 ring-sand-200 transition-opacity duration-300 ${loaded || isPlaceholder ? "opacity-100" : "opacity-0"}`}
        loading="lazy"
        onLoad={() => setLoaded(true)}
      />
    </div>
  );
};

/**
 * LazyImage — shows skeleton shimmer while image loads
 */
function LazyImage({ src, alt, className = "" }) {
  const [loaded, setLoaded] = useState(false);
  return (
    <div className="relative">
      {!loaded && (
        <div className={`absolute inset-0 animate-pulse bg-gradient-to-r from-sand-200 via-sand-100 to-sand-200 bg-[length:800px_100%] ${className}`} />
      )}
      <img
        src={src}
        alt={alt || ""}
        loading="lazy"
        onLoad={() => setLoaded(true)}
        className={`${className} transition-opacity duration-300 ${loaded ? "opacity-100" : "opacity-0"}`}
      />
    </div>
  );
}

/**
 * Reusable profile card used across Matches, Interests, Shortlist, etc.
 * Variant: 'grid' | 'list' | 'compact'
 */
export default function ProfileCard({
  profile,
  variant = "list",
  showVerified = true,
  showPremium = true,
  badge = null,
  actions = null,
  matchesStyle = variant === "grid",
  overlayActions = false,
  href,
  onClick,
}) {
  const location = useLocation();
  const verified = showVerified && isVerifiedProfile(profile);
  const premium = showPremium && isPremiumActive(profile);
  const photo = getProfileDisplayImage(profile);
  const isPlaceholder = shouldShowProfilePlaceholder(profile);
  const linkTo = href ?? createProfilePath(profile);
  const linkState = {
    from: `${location.pathname}${location.search}`,
    profile,
  };

  // ===================== GRID =====================
  if (variant === "grid") {
    return (
      <motion.article
        initial={{ opacity: 0, y: 14 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.3 }}
        whileHover={{ y: -4 }}
        className={`group relative overflow-hidden rounded-3xl border border-sand-200 bg-white shadow-soft transition-all hover:shadow-elevated ${matchesStyle ? "rounded-[22px]" : ""}`}
      >
        <Link to={linkTo} state={linkState} onClick={onClick} className="relative block">
          {isPlaceholder ? (
            <img
              src={photo}
              alt={profile.fullName || "Profile"}
              className="aspect-[4/5] w-full object-cover object-center transition-transform duration-500 group-hover:scale-[1.03]"
            />
          ) : (
            <LazyImage
              src={photo}
              alt={profile.fullName}
              className="aspect-[4/5] w-full object-cover object-center transition-transform duration-500 group-hover:scale-[1.03]"
            />
          )}
          <div className="pointer-events-none absolute inset-x-0 bottom-0 h-2/3 bg-gradient-to-t from-black/65 via-black/15 to-transparent" />

          <div className="absolute left-3 top-3 flex flex-wrap gap-1.5">
            {verified && (
              <span
                aria-label="Verified"
                title="Verified"
                className={`inline-flex items-center gap-1 rounded-full bg-emerald-500/95 text-white shadow-soft ${
                  matchesStyle ? "h-7 w-7 justify-center" : "px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide"
                }`}
              >
                <ShieldCheck size={matchesStyle ? 15 : 11} />
                {!matchesStyle && "Verified"}
              </span>
            )}
            {premium && (
              <span
                aria-label="Premium"
                title="Premium"
                className={`inline-flex items-center gap-1 rounded-full bg-amber-400 text-amber-900 shadow-soft ${
                  matchesStyle ? "h-7 w-7 justify-center" : "px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide"
                }`}
              >
                <Crown size={matchesStyle ? 15 : 11} />
                {!matchesStyle && "Premium"}
              </span>
            )}
          </div>

          <div className={`absolute inset-x-0 bottom-0 text-white ${matchesStyle ? "p-3" : "p-4"}`}>
            <h2 className={`truncate font-display font-bold ${matchesStyle ? "text-[15px]" : "text-lg"}`}>{profile.fullName || "Profile"}</h2>
            {profile.kmProfileId && (
              <span className="mt-0.5 inline-block text-[10.5px] font-semibold text-white/70">{profile.kmProfileId}</span>
            )}
            {!matchesStyle && (
              <div className="mt-1 flex items-center gap-3 text-[12px] text-white/85">
                <span className="inline-flex items-center gap-1"><CalendarDays size={12} />{formatDate(profile.dateOfBirth)}</span>
                <span className="inline-flex min-w-0 items-center gap-1">
                  <MapPin size={12} />
                  <span className="truncate">{profile.presentResidence || profile.workingPlace || "—"}</span>
                </span>
              </div>
            )}
          </div>
        </Link>

        {overlayActions && actions && (
          <div className="absolute right-3 top-3 z-10">
            {actions}
          </div>
        )}

        <div className={matchesStyle ? "space-y-2.5 p-3" : "space-y-3 p-4"}>
          {matchesStyle ? (
            <div className="space-y-1.5 text-[11px] leading-4 text-ink-500">
              <div className="flex min-w-0 flex-wrap gap-1">
                {profile.dateOfBirth && (
                  <span className="rounded-full bg-sand-100 px-2 py-0.5 text-[10px] font-medium text-ink-500">{formatDate(profile.dateOfBirth)}</span>
                )}
              </div>
              {profile.caste && (
                <div className="flex min-w-0 items-center gap-1.5">
                  <span className="h-1.5 w-1.5 shrink-0 rounded-full bg-brand-500" />
                  <span className="truncate">{profile.caste}</span>
                </div>
              )}
              {(profile.presentResidence || profile.workingPlace || profile.state) && (
                <div className="flex min-w-0 items-center gap-1.5">
                  <MapPin size={12} className="shrink-0 text-brand-500" />
                  <span className="truncate">{profile.presentResidence || profile.workingPlace || profile.state}</span>
                </div>
              )}
              {(profile.education || profile.profession) && (
                <div className="flex min-w-0 items-center gap-1.5">
                  {profile.education ? <GraduationCap size={12} className="shrink-0 text-brand-500" /> : <Briefcase size={12} className="shrink-0 text-brand-500" />}
                  <span className="truncate">{profile.education || profile.profession}</span>
                </div>
              )}
              {badge}
            </div>
          ) : (
            <div className="flex flex-wrap gap-1.5">
              {badge}
              {profile.caste && <span className="chip-outline">{profile.caste}</span>}
              {profile.profession && <span className="chip">{profile.profession}</span>}
            </div>
          )}

          {matchesStyle && (
            <Link to={linkTo} state={linkState} onClick={onClick} className="inline-flex items-center gap-1 pt-0.5 text-[11px] font-bold text-brand-600 hover:text-brand-700">
              View Profile <span aria-hidden="true">→</span>
            </Link>
          )}

          {actions && !overlayActions && (
            <div className="flex flex-wrap gap-2 pt-1">
              {actions}
            </div>
          )}
        </div>
      </motion.article>
    );
  }

  // ===================== COMPACT (small) =====================
  if (variant === "compact") {
    return (
      <motion.div
        layout
        initial={{ opacity: 0, y: 8 }}
        animate={{ opacity: 1, y: 0 }}
        className="card flex items-center gap-3 p-3 transition hover:shadow-elevated"
      >
        <ProfileAvatar profile={profile} size={56} />
        <Link to={linkTo} state={linkState} onClick={onClick} className="min-w-0 flex-1">
          <div className="flex items-center gap-1.5">
            <h3 className="truncate font-display text-[15px] font-bold text-ink-800">{profile.fullName}</h3>
            {profile.kmProfileId && <span className="text-[10px] font-bold text-ink-400">{profile.kmProfileId}</span>}
            {verified && <ShieldCheck size={12} className="text-emerald-500 shrink-0" />}
            {premium && <Crown size={12} className="text-amber-500 shrink-0" />}
          </div>
          <p className="truncate text-[12px] text-ink-400">
            {profile.profession || profile.education || profile.presentResidence || "—"}
          </p>
        </Link>
        {actions}
      </motion.div>
    );
  }

  // ===================== LIST (rich row) =====================
  return (
    <motion.article
      layout
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      className="card overflow-hidden transition hover:shadow-elevated"
    >
      <div className="flex flex-col gap-3 p-4 sm:flex-row sm:items-center sm:p-5">
        <div className="flex flex-1 items-center gap-3 sm:gap-4">
          <Link to={linkTo} state={linkState} onClick={onClick} className="shrink-0">
            <ProfileAvatar profile={profile} size={60} />
          </Link>
          <div className="min-w-0 flex-1">
            <Link to={linkTo} state={linkState} onClick={onClick} className="flex flex-wrap items-center gap-1.5">
              <h2 className="truncate font-display text-[15px] font-bold text-ink-800">
                {profile.fullName || "Profile"}
              </h2>
              {profile.kmProfileId && (
                <span className="rounded-full bg-sand-100 px-2 py-0.5 text-[10.5px] font-bold text-ink-500">
                  {profile.kmProfileId}
                </span>
              )}
              {verified && (
                <span className="inline-flex items-center gap-1 rounded-full bg-emerald-50 px-2 py-0.5 text-[10px] font-bold uppercase text-emerald-700">
                  <ShieldCheck size={10} /> Verified
                </span>
              )}
              {premium && (
                <span className="inline-flex items-center gap-1 rounded-full bg-amber-100 px-2 py-0.5 text-[10px] font-bold uppercase text-amber-700">
                  <Crown size={10} /> Premium
                </span>
              )}
              {badge}
            </Link>
            <div className="mt-0.5 flex flex-wrap items-center gap-x-2.5 gap-y-0.5 text-[12px] text-ink-400">
              <span className="inline-flex items-center gap-1"><CalendarDays size={11} />{formatDate(profile.dateOfBirth)}</span>
              {profile.caste && <span className="inline-flex items-center gap-1">{profile.caste}</span>}
              {profile.profession && <span className="inline-flex items-center gap-1"><Briefcase size={11} />{profile.profession}</span>}
              {(profile.presentResidence || profile.state) && (
                <span className="inline-flex min-w-0 items-center gap-1">
                  <MapPin size={11} />
                  <span className="truncate">{profile.presentResidence || profile.state}</span>
                </span>
              )}
            </div>
          </div>
        </div>

        {actions && (
          <div className="flex items-center gap-2 sm:justify-end">
            <Link
              to={linkTo}
              state={linkState}
              onClick={onClick}
              className="inline-flex items-center justify-center gap-1.5 rounded-2xl border border-sand-300 bg-white px-3.5 py-2.5 text-[12.5px] font-semibold text-ink-700 transition hover:border-brand-600 hover:text-brand-600"
            >
              <Eye size={14} /> View
            </Link>
            {actions}
          </div>
        )}
      </div>
    </motion.article>
  );
}
